'use client';

import BookDemoModal from '@/components/RevampedLanding/BookDemo';
import StickyEnrollBar from '@/components/RevampedLanding/StickyEnrollBar';
import usePageType from '@/hooks/useLandingPageType';
import { eventTracker } from '@/lib/events/cjrEventTracker';
import { isFromApp } from '@/utils/utils';
import createWidget, { IPageDataContext } from '@/widgets/create-widget';
import { PhotoIcon } from '@heroicons/react/24/solid';
import { JSX, useEffect, useState } from 'react';
import StickyEnrollBarSchema, {
  StickyEnrollBarDefaultValues,
  StickyEnrollBarProps,
} from './schema';

export function StickyEnrollBarWidget(props: StickyEnrollBarProps & IPageDataContext) {
  const { bottomStickyBar, pageData } = props;

  const [isBookDemoModalOpen, setIsBookDemoModalOpen] = useState(false);
  const [isStickyVisible, setIsStickyVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);

  const { currentPageType, fullPath } = usePageType();

  const rawMin = Number(bottomStickyBar?.minHeight) || 0;
  const rawMax = Number(bottomStickyBar?.maxHeight) || 0;
  const minHeight = rawMin > 100 ? rawMin : 600;
  const maxHeight = rawMax > 100 ? rawMax : 99999;

  const handleOpenBookDemoModal = ({ clickFrom }: { clickFrom?: string }) => {
    eventTracker.bookADemoClick({
      clickFrom: clickFrom as
        | 'sticky'
        | 'hero_section'
        | 'cohorts_section'
        | 'product_features_section',
      came_from: currentPageType,
      page_link: fullPath as string,
    });
    setIsBookDemoModalOpen(true);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;

      if (scrollTop < minHeight) {
        setIsStickyVisible(false);
        return;
      }

      if (scrollTop > minHeight) {
        if (scrollTop > maxHeight) {
          setIsStickyVisible(false);
          return;
        }
        setIsStickyVisible(true);
        setHasBeenVisible(true);
      }
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }

    return () => {};
  }, [minHeight, maxHeight]);

  return (
    <>
      <StickyEnrollBar
        handleOpenBookDemoModal={handleOpenBookDemoModal}
        isStickyVisible={isStickyVisible}
        hasBeenVisible={hasBeenVisible}
        bottomStickyBarData={props}
      />
      {isBookDemoModalOpen && (
        <BookDemoModal
          activeState="select_goal"
          open={isBookDemoModalOpen}
          onClose={() => setIsBookDemoModalOpen(false)}
          goals={pageData?.curiousJrLandingData?.data?.goalSection?.goals || []}
          isOtplessEnabled={pageData?.curiousJrLandingData?.data?.isOtplessEnabled}
          appRedirectionScreen={pageData?.curiousJrLandingData?.data?.appRedirectionScreen}
          shouldTriggerTruecaller={
            pageData?.curiousJrLandingData?.data?.shouldTriggerTruecaller || false
          }
          pageSource={isFromApp() ? 'APP' : 'LANDING'}
        />
      )}
    </>
  );
}

export default createWidget<StickyEnrollBarProps>({
  formType: StickyEnrollBarSchema,
  render(props, pageData): JSX.Element {
    return <StickyEnrollBarWidget {...props} pageData={pageData} />;
  },
  id: 'Landing.Page.StickyEnrollBar',
  name: 'Landing Sticky Enroll Bar',
  icon: <PhotoIcon className="w-10 h-10" />,
  defaultProps: StickyEnrollBarDefaultValues,
});
// Duplicate StickyEnrollBarWidget removed
